import { theme } from '@Guidelines/Theme/theme';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import type { ReviewStage } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useReviewStage';
import { css } from '@emotion/react';
import * as React from 'react';

const noteStyle = css`
	margin: 0;
	padding: 0.75rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.accentSurface};
	color: ${theme.colors.accentDark};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.55;
`;

type Props = {
	readonly reviewStage: ReviewStage;
};

/** The sentence under the stage's tabs: what the screen will do with the reader's corrections, or why it
 * cannot yet be reviewed. */
const ReviewStageNote: React.FC<Props> = ({ reviewStage }) => {
	const canEdit = useCanEditDocument();
	const { documentTypes } = useAccountDocumentTypes();
	const { documents } = reviewStage;

	const message = React.useMemo(() => {
		const failedDocument = documents.find((candidate) => candidate.isFailed);

		if (failedDocument !== undefined) {
			return `« ${documentTypes?.labelOf(failedDocument.documentTypeId) ?? 'Document'} » n’a pas pu être généré : réessayez depuis la préparation.`;
		}

		if (documents.some((candidate) => candidate.documentId === null)) {
			return 'Les documents de l’étape ne sont pas encore tous générés.';
		}

		if (documents.every((candidate) => !candidate.isToReview)) {
			return 'Document vérifié : toute correction rouvre sa vérification.';
		}

		if (!canEdit) {
			return 'Ce document est en lecture seule.';
		}

		return 'Vos corrections sont conservées dans le document. Validez-le quand il est prêt.';
	}, [documents, canEdit, documentTypes]);

	if (documents.length === 0) {
		return null;
	}

	return (
		<p css={noteStyle} role="note">
			{message}
		</p>
	);
};

export default ReviewStageNote;
